import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  capacidadeDaMaquina,
  CODIGOS_ERRO,
  combinarModelos,
  FRASES_DOS_ERROS_DO_OPENCODE,
  modeloPadrao,
  ROTAS,
  type EstadoDaConfigOpencode,
} from '@remoto/shared';
import { MOTORES, ROTULO_DO_MOTOR, type Motor, type ProvedorOpencode } from '@remoto/protocolo';
import { chamar, ErroApi } from '../api';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { Modal } from '../componentes/Modal';
import { SeloForaDeCasa } from '../componentes/SeloForaDeCasa';
import { usarEstadoDaConfig } from '../configuracoes/dados';
import { mensagemDoErro } from '../erros';
import {
  avisoDoBypassDaMestre,
  avisoDoModoDaMestre,
  fraseDaFaltaDeChave,
  mestrePodeNascerEmBypass,
  motorSaiDeCasa,
  perfilDaMestre,
  rotuloDoEsforco,
  textoDaConfirmacaoOpencode,
  type PerfilDaMestre,
} from '../sessoes/criacao-por-motor';

interface MaquinaParaMestre {
  id: string;
  nome: string;
  papel: string;
  estado: string;
  capacidades?: unknown;
  /** O bypass total (ADR-0023) valendo aqui, resolvido pela API: é o que abre o "nascer em bypass". */
  bypassTotal?: boolean;
  /** O retrato do Codex, com o catálogo — a mestre do Codex precisa de modelo. */
  codex?: { modelos?: { id: string; rotulo?: string; padrao?: boolean }[] } | null;
}

/** A tela decide a mensagem pelo código, nunca pelo texto cru do servidor. */
const MENSAGEM_POR_CODIGO: Record<string, string> = {
  mestre_ja_existe: 'Já existe uma sessão mestre deste motor. Encerre a atual antes de criar outra.',
  codex_indisponivel:
    'Esta máquina não fala Codex (CODEX_HABILITADO desligado, binário ausente ou daemon antigo).',
  modelo_obrigatorio_no_motor: 'Escolha o modelo: no Codex ele não tem padrão da casa.',
  mestre_indisponivel:
    'Esta máquina não hospeda o mestre (MESTRE_HABILITADO desligado ou daemon antigo).',
  mestre_sem_confirmacao: 'Confirme que entendeu o modo automático para criar.',
  cwd_fora_da_raiz: 'Esta pasta não está liberada nesta máquina.',
  vps_offline: 'A máquina está desligada.',
  vps_sem_memoria: 'A máquina está sem memória livre suficiente agora.',
};

/**
 * A configuração do OpenCode, como este diálogo precisa dela: o modelo e o
 * esforço da mestre (D2), se a frota tem a chave do provedor ATIVO e qual é
 * ele (F2) — o selo e a confirmação nomeiam o provedor para onde a conversa
 * vai. `null` enquanto não se sabe.
 */
type ConfigDaMestreOpencode =
  | { estado: 'lendo' }
  | { estado: 'recurso_desligado' }
  | { estado: 'ilegivel' }
  | {
      estado: 'pronta';
      modelo: string;
      esforco: string;
      temChave: boolean;
      provedor: ProvedorOpencode;
    };

/**
 * O que a leitura da configuração deu. Ela já chega conferida contra o
 * contrato (`usarEstadoDaConfig`, da tela de Configurações): a forma errada
 * volta como erro, e erro que não é o 404 do recurso é "ilegível".
 */
function lerConfigDaMestre(
  dados: EstadoDaConfigOpencode | undefined,
  erro: unknown,
): ConfigDaMestreOpencode {
  if (
    erro instanceof ErroApi &&
    (erro.codigo === CODIGOS_ERRO.recursoOpencodeDesligado || erro.status === 404)
  ) {
    return { estado: 'recurso_desligado' };
  }
  if (erro) return { estado: 'ilegivel' };
  if (dados === undefined) return { estado: 'lendo' };
  return {
    estado: 'pronta',
    modelo: dados.config.modeloMestre,
    esforco: dados.config.esforcoMestre,
    temChave: dados.chave !== null,
    provedor: dados.config.provedor,
  };
}

/**
 * O que impede criar a mestre deste motor ANTES de mandar — ou `null`.
 *
 * Só os motivos que a tela sabe de certeza: o recurso desligado e a frota sem
 * chave. Configuração ilegível não fecha a porta, porque quem manda no modelo
 * da mestre é a API (`modelo_da_mestre_vem_da_config`); a tela só mostra.
 */
function bloqueioDaConfig(perfil: PerfilDaMestre, config: ConfigDaMestreOpencode): string | null {
  if (perfil.modelo !== 'configuracoes') return null;
  if (config.estado === 'recurso_desligado') {
    return FRASES_DOS_ERROS_DO_OPENCODE[CODIGOS_ERRO.recursoOpencodeDesligado];
  }
  if (config.estado === 'pronta' && !config.temChave) {
    // Com o nome do provedor ATIVO (F2): a tela de Configurações tem duas chaves.
    return fraseDaFaltaDeChave(config.provedor);
  }
  return null;
}

/**
 * Criação da sessão mestre (F13), num diálogo — não numa rota: quem cria já
 * está olhando a lista onde ela vai aparecer fixada.
 *
 * A confirmação explícita não é burocracia: a mestre nasce num modo em que os
 * despachos dela (mandar mensagem a outra sessão, criar sessão) fluem sem
 * cartão de permissão. Quem liga isso precisa dizer que sabe — e a do
 * OpenCode, que nasce em modo livre FIXO e fala pelo provedor de modelos da
 * frota (F2: o NanoGPT; o OpenRouter, guardado), pede as duas
 * confirmações (D3, D10).
 */
export function CriarMestre({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}): React.JSX.Element | null {
  const cliente = useQueryClient();
  const [maquinaId, setMaquinaId] = useState('');
  const [motor, setMotor] = useState<Motor>('CLAUDE');
  const [modelo, setModelo] = useState('');
  const [cwd, setCwd] = useState(perfilDaMestre('CLAUDE').pastaPadrao);
  const [confirmado, setConfirmado] = useState(false);
  const [confirmacaoOpencode, setConfirmacaoOpencode] = useState(false);
  const [emBypass, setEmBypass] = useState(false);
  const [confirmacaoBypass, setConfirmacaoBypass] = useState(false);
  const [erro, setErro] = useState('');

  const perfil = perfilDaMestre(motor);
  const saiDeCasa = motorSaiDeCasa(motor);

  const { data: maquinas = [] } = useQuery({
    queryKey: ['maquinas'],
    queryFn: () => chamar<MaquinaParaMestre[]>(ROTAS.maquinas),
    enabled: aberto,
  });

  /*
    O modelo da mestre do OpenCode vem da tela de Configurações (D2) — a
    mesma rota que só ADMIN lê, e só ADMIN cria mestre. É lida aqui para a
    pessoa VER o que vai nascer, nunca para escolher, e pela MESMA consulta
    daquela tela: o que o dono acabou de salvar lá é o que aparece aqui.
  */
  const consultaConfig = usarEstadoDaConfig(aberto && perfil.modelo === 'configuracoes');
  const config = lerConfigDaMestre(consultaConfig.data, consultaConfig.error);
  const bloqueio = bloqueioDaConfig(perfil, config);
  const provedorAtivo = config.estado === 'pronta' ? config.provedor : null;

  /*
    A capacidade é do MOTOR: `mestre`, `mestreCodex`, `mestreOpencode`. E o
    papel não entra mais — a mestre vive num PC desde a D51 (ADR-0015), com
    `MESTRE_PERMITE_PC` ligada nos dois lados, e filtrar por `papel === 'VPS'`
    fazia o painel esconder a máquina que o daemon aceitaria. A do OpenCode
    mora na VPS (D2), e num PC só com a mesma chave: quem diz é a sonda.

    Se o daemon recusar mesmo assim, a criação devolve o código próprio e a
    mensagem sai do dicionário.
  */
  const elegiveis = maquinas.filter(
    (m) => capacidadeDaMaquina(m.capacidades, perfil.capacidade)?.estado === 'aprovada',
  );

  const escolhida = elegiveis.find((m) => m.id === maquinaId);
  const modelosOferecidos = combinarModelos(
    perfil.modelo === 'catalogo-da-maquina' ? (escolhida?.codex?.modelos ?? []) : [],
    motor,
  );
  const semCatalogo =
    perfil.modelo === 'catalogo-da-maquina' && maquinaId !== '' && modelosOferecidos.length === 0;
  // ADR-0023: a opção só existe onde a API diz que o bypass total vale nesta máquina.
  const podeBypass = mestrePodeNascerEmBypass(motor, escolhida?.bypassTotal);
  const pedeBypass = podeBypass && emBypass;

  function escolherMaquina(id: string): void {
    setMaquinaId(id);
    // A escolha é DAQUELA máquina: em outra o bypass pode nem existir.
    setEmBypass(false);
    setConfirmacaoBypass(false);
  }

  function escolherMotor(novo: Motor): void {
    setMotor(novo);
    setMaquinaId('');
    setEmBypass(false);
    setConfirmacaoBypass(false);
    setModelo(modeloPadrao(novo, []) ?? '');
    setConfirmacaoOpencode(false);
    // A pasta padrão é da VPS e do Claude; num PC ela não existe, e deixar o
    // caminho de Linux num Windows renderia `cwd_fora_da_raiz` depois de tudo
    // pronto. E duas mestres na mesma pasta dividiriam a memória.
    setCwd(perfilDaMestre(novo).pastaPadrao);
  }

  /** Só o que cada motor aceita: o Codex exige modelo; o OpenCode o recusa. */
  function camposDoModelo(): Record<string, unknown> {
    switch (perfil.modelo) {
      case 'politica-da-casa':
        return {};
      case 'catalogo-da-maquina':
        return { modelo };
      case 'configuracoes':
        return { confirmacaoOpencode };
    }
  }

  const criar = useMutation({
    mutationFn: () =>
      chamar<{ id: string }>(ROTAS.sessoes + '/mestre', {
        method: 'POST',
        corpo: {
          maquinaId,
          cwd,
          confirmacaoMestre: true,
          motor,
          ...camposDoModelo(),
          ...(pedeBypass ? { modo: 'bypassPermissions', confirmacaoModoPerigoso: confirmacaoBypass } : {}),
        },
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['sessoes'] });
      aoFechar();
    },
    onError: (falha) => {
      setErro(
        mensagemDoErro(falha, 'Não deu para criar a sessão mestre agora.', {
          ...MENSAGEM_POR_CODIGO,
          // Com o NOME do provedor ativo (F2): a tela de Configurações tem duas chaves.
          opencode_sem_chave: fraseDaFaltaDeChave(provedorAtivo),
        }),
      );
    },
  });

  if (!aberto) return null;
  const pronto =
    maquinaId !== '' &&
    cwd.trim() !== '' &&
    confirmado &&
    !criar.isPending &&
    bloqueio === null &&
    (perfil.modelo !== 'catalogo-da-maquina' || modelo !== '') &&
    (!saiDeCasa || confirmacaoOpencode) &&
    (!pedeBypass || confirmacaoBypass);

  return (
    <Modal
      titulo="Criar sessão mestre"
      aberto={aberto}
      aoFechar={aoFechar}
      rodape={
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={aoFechar}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              setErro('');
              criar.mutate();
            }}
            disabled={!pronto}
            className="rounded-md bg-zinc-100 px-3 py-1.5 text-sm font-medium text-zinc-900 disabled:opacity-40"
          >
            {criar.isPending ? 'criando…' : 'Criar mestre'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-zinc-400">
          A sessão mestre conhece a frota inteira: consulta qualquer conversa, busca em tudo, lê o
          .claude das máquinas, despacha mensagens e cria sessões para delegar.
        </p>

        <CampoDeEscolha<Motor>
          rotulo="Motor"
          valor={motor}
          opcoes={MOTORES.map((id) => ({ id, rotulo: ROTULO_DO_MOTOR[id] }))}
          aoEscolher={escolherMotor}
        />
        {saiDeCasa ? <SeloForaDeCasa provedor={provedorAtivo} /> : null}

        {perfil.modelo === 'configuracoes' ? (
          /*
            Só leitura, com o caminho para mudar (D2, D17). Um seletor aqui
            seria uma segunda resposta para "qual modelo a mestre usa" — e a
            API recusaria o que ele mandasse.
          */
          <div
            className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-400"
            data-teste="modelo-da-mestre-da-config"
          >
            {config.estado === 'pronta' ? (
              <p>
                Modelo da mestre: <span className="font-mono text-zinc-200">{config.modelo}</span> ·
                esforço: <span className="text-zinc-200">{rotuloDoEsforco(config.esforco)}</span>
              </p>
            ) : config.estado === 'lendo' ? (
              <p>Lendo o modelo da mestre na configuração…</p>
            ) : (
              <p>Não deu para ler a configuração do OpenCode agora.</p>
            )}
            <p className="mt-1">
              O modelo e o esforço da mestre se escolhem em{' '}
              <Link
                to="/admin/configuracoes"
                onClick={aoFechar}
                className="text-sky-300 underline hover:text-sky-200"
              >
                Configurações
              </Link>
              , não aqui.
            </p>
          </div>
        ) : null}

        {bloqueio ? (
          <p
            className="rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-amber-200"
            data-teste="mestre-bloqueada"
          >
            {bloqueio}
          </p>
        ) : elegiveis.length === 0 ? (
          <p
            className="rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-amber-200"
            data-teste="sem-maquina-elegivel"
          >
            Nenhuma máquina reportou o mestre {ROTULO_DO_MOTOR[motor]}. Ligue a chave no daemon
            daquela máquina — e, num PC, também <code>MESTRE_PERMITE_PC</code> nos dois lados.
          </p>
        ) : (
          <>
            <CampoDeEscolha
              rotulo="Máquina"
              valor={maquinaId}
              opcoes={elegiveis.map((m) => ({ id: m.id, rotulo: m.nome }))}
              aoEscolher={escolherMaquina}
            />

            {perfil.modelo === 'catalogo-da-maquina' ? (
              semCatalogo ? (
                <p
                  className="rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-amber-200"
                  data-teste="sem-catalogo-do-mestre"
                >
                  Esta máquina ainda não reportou os modelos do {ROTULO_DO_MOTOR[motor]}. Sem a
                  lista não dá para escolher um, e inventar um nome faria a mestre nascer
                  quebrada.
                </p>
              ) : (
                <CampoDeEscolha
                  rotulo="Modelo"
                  valor={modelo || null}
                  opcoes={modelosOferecidos.map((m) => ({ id: m.id, rotulo: m.rotulo }))}
                  aoEscolher={setModelo}
                />
              )
            ) : null}
            <label className="block">
              <span className="mb-1 block text-xs text-zinc-500">Pasta de trabalho (a memória dele)</span>
              <input
                value={cwd}
                onChange={(e) => setCwd(e.target.value)}
                className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 font-mono text-xs text-zinc-200"
              />
            </label>
            <CaixaDeMarcar marcada={confirmado} aoMarcar={setConfirmado}>
              {avisoDoModoDaMestre(motor)}
            </CaixaDeMarcar>
            {podeBypass ? (
              /*
                ADR-0023: nascer em bypass é gesto explícito, em dois passos —
                escolher e confirmar o modo perigoso. A frase diz o efeito
                daquele lugar (no Codex da VPS o sandbox continua).
              */
              <div className="space-y-2" data-teste="mestre-em-bypass">
                <CaixaDeMarcar
                  marcada={emBypass}
                  aoMarcar={(v) => {
                    setEmBypass(v);
                    if (!v) setConfirmacaoBypass(false);
                  }}
                >
                  Nascer em «Ignorar permissões» (bypass total)
                </CaixaDeMarcar>
                {emBypass ? (
                  <CaixaDeMarcar marcada={confirmacaoBypass} aoMarcar={setConfirmacaoBypass}>
                    {avisoDoBypassDaMestre(motor, escolhida?.papel ?? '')}
                  </CaixaDeMarcar>
                ) : null}
              </div>
            ) : null}
            {saiDeCasa ? (
              <CaixaDeMarcar marcada={confirmacaoOpencode} aoMarcar={setConfirmacaoOpencode}>
                {textoDaConfirmacaoOpencode(provedorAtivo)}
              </CaixaDeMarcar>
            ) : null}
          </>
        )}

        {erro ? <p className="text-rose-300">{erro}</p> : null}
      </div>
    </Modal>
  );
}
